import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { usePartnerAuth } from "../context/PartnerAuthContext";
import { PartnerApiError } from "../lib/api";
import { AuthLayout, PoweredBy } from "../components/AuthLayout";
import { useNative } from "../../shared/phone/media";
import { LoginNative } from "./AuthNative";

/**
 * Real partner login. Verifies email + password against the partner API and
 * lands on the programs picker. No demo shortcut, no fabricated session.
 */
export function LoginView() {
  const { login } = usePartnerAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const native = useNative();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login({ email, password });
      navigate("/programs", { replace: true });
    } catch (err) {
      setError(err instanceof PartnerApiError ? err.message : "Could not log in. Try again.");
    } finally {
      setBusy(false);
    }
  }

  if (native) {
    return (
      <LoginNative
        email={email}
        password={password}
        onEmail={setEmail}
        onPassword={setPassword}
        error={error}
        busy={busy}
        onSubmit={onSubmit}
      />
    );
  }

  return (
    <AuthLayout>
      <form className="auth-card" onSubmit={onSubmit}>
        <div className="eyebrow">Welcome back</div>
        <h1>Log in</h1>
        <p className="sub">Pick up where you left off.</p>

        {error && <div className="auth-error" role="alert">{error}</div>}

        <div className="field">
          <label htmlFor="li-email">Email</label>
          <input
            id="li-email"
            className="inp"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="li-pass">Password</label>
          <input
            id="li-pass"
            className="inp"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        <button className="btn wide" type="submit" disabled={busy}>
          {busy ? "Logging in..." : "Log in"}
        </button>
        <div className="auth-alt">
          New here? <Link to="/signup">Create an account</Link>
        </div>
        <PoweredBy />
      </form>
    </AuthLayout>
  );
}
